import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';

import { cloneDeep, defaults, find, isEqual } from 'lodash';

import { FilterConfig } from './filter-config';
import { FilterEvent } from './filter-event';
import { FilterField } from './filter-field';
import { FilterQuery } from './filter-query';

/**
 * Helper component for the filter query field and filter query dropdown
 */

export class FilterFieldsComponent implements DoCheck, OnInit {
  /**
   * The filter config containing component properties
   */
   config: FilterConfig;

  /**
   * The event emitted when a filter has been added
   */
   onAdd = new EventEmitter();

  /**
   * The event emitted when a saved filter has been deleted
   */
   onDelete = new EventEmitter();

  /**
   * The event emitted when a field menu option is selected
   */
   onFieldSelect = new EventEmitter();

  /**
   * The event emitted when the user types ahead in the query input field
   */
   onTypeAhead = new EventEmitter();

  private _currentField: FilterField;
  private _currentValue: string;
  private defaultConfig = {
    disabled: false
  } as FilterConfig;
  private prevConfig: FilterConfig;

  /**
   * The default constructor
   */
  constructor() {
  }

  // Initialization

  /**
   * Setup component configuration upon initialization
   */
  ngOnInit(): void {
    this.setupConfig();
  }

  /**
   * Check if the component config has changed
   */
  ngDoCheck(): void {
    // Do a deep compare on config
    if (!isEqual(this.config, this.prevConfig)) {
      this.setupConfig();
    }
  }

  /**
   * Set up default config
   */
  protected setupConfig(): void {
    if (this.config !== undefined) {
      defaults(this.config, this.defaultConfig);
    } else {
      this.config = cloneDeep(this.defaultConfig);
    }

    if (this.config && this.config.fields === undefined) {
      this.config.fields = [];
    }
    if (this.config && this.config.tooltipPlacement === undefined) {
      this.config.tooltipPlacement = 'top';
    }
    this.initCurrentField();
    this.prevConfig = cloneDeep(this.config);
  }

  /**
   * Initialize current field and value
   */
  protected initCurrentField(): void {
    let fieldFound: boolean = false;
    if (this._currentField !== undefined) {
      this.config.fields.forEach((nextField) => {
        if (nextField.id === this._currentField.id) {
          fieldFound = true;
          return;
        }
      });
    }
    if (!fieldFound) {
      this._currentField = this.config.fields[0];
      this._currentValue = null;
    } else if (this._currentField.type === 'select' || this._currentField.type === 'typeahead') {
      // clear dropdown if there is no applied filter for it
      if (!this.getAppliedFilterByField(this._currentField)) {
        this._currentValue = null;
      }
    }
    if (this._currentValue === undefined) {
      this._currentValue = null;
    }
  }

  protected getAppliedFilterByField(field: any): any {
    let foundFilter = find(this.config.appliedFilters, {
      field: field
    });
    return foundFilter;
  }

  /**
   * Reset current field and value
   */
  reset(): void {
    this._currentField = undefined;
    this.initCurrentField();
  }

  // Accessors

  /**
   * Get the current filter field
   *
   * @returns {FilterField} The current filter field
   */
  get currentField(): FilterField {
    return this._currentField;
  }

  /**
   * Get the current filter field value
   *
   * @returns {string} The current filter field value
   */
  protected get currentValue(): string {
    return this._currentValue;
  }

  /**
   * Set the current filter field value
   *
   * @param val The current filter field value
   */
  protected set currentValue(val: string) {
    this._currentValue = val;
  }

  // Private

  private deleteQuery($event: MouseEvent, filterQuery: FilterQuery, el: HTMLElement): void {
    // Unset focus
    if (el !== undefined) {
      el.blur();
    }

    // Close previous open confirmation
    this.hideDeleteConfirm(false);

    // Show delete query confirmation
    (filterQuery as any).showDeleteConfirm = true;

    // Menu should remain open
    $event.stopPropagation();
  }

  private deleteQueryCancel($event: MouseEvent, filterQuery: FilterQuery): void {
    // Hide delete query confirmation
    (filterQuery as any).showDeleteConfirm = false;

    // Menu should remain open
    $event.stopPropagation();
  }

  private deleteQueryConfirm($event: MouseEvent, filterQuery: FilterQuery): void {
    // Hide delete query confirmation
    (filterQuery as any).showDeleteConfirm = false;

    // Menu should remain open
    if (this._currentField.queries.length > 1) {
      $event.stopPropagation();
    }
    this.onDelete.emit({
      field: this._currentField,
      query: filterQuery,
      value: filterQuery.value
    } as FilterEvent);
    this._currentValue = null;
  }

  private fieldInputKeyPress($event: KeyboardEvent): void {
    if ($event.which === 13 && this._currentValue && this._currentValue.length > 0) {
      this.onAdd.emit({
        field: this._currentField,
        value: this._currentValue
      } as FilterEvent);
      this._currentValue = undefined;
    }
  }

  // Hide all delete confirm
  private hideDeleteConfirm(isOpen: boolean): void {
    this._currentField.queries.forEach(query => {
      (query as any).showDeleteConfirm = false;
    });
  }

  private isFieldDisabled(field: FilterField): boolean {
    if (field.disabled) {
      return true;
    }
    if (field.type === undefined || field.type === 'text') {
      return false;
    }
    return (field.queries === undefined || field.queries.length === 0);
  }

  private queryInputChange(value: string) {
    this.onTypeAhead.emit({
      field: this._currentField,
      value: this._currentValue
    } as FilterEvent);
  }

  private selectField(field: FilterField): void {
    this._currentField = field;
    if (this._currentField.type === 'select' || this._currentField.type === 'typeahead') {
      // Restore selected value for dropdown if there is an applied filter for it
      let filterField: any = this.getAppliedFilterByField(this._currentField);
      this._currentValue = filterField ? filterField.value : null;
    } else {
      this._currentValue = null;
    }
    this.onFieldSelect.emit({
      field: this._currentField,
      value: this._currentValue
    } as FilterEvent);
  }

  private selectQuery(filterQuery: FilterQuery): void {
    this.onAdd.emit({
      field: this._currentField,
      query: filterQuery,
      value: filterQuery.value
    } as FilterEvent);
    this._currentValue = filterQuery.value;
  }

  private showDelete(): boolean {
    let result = false;
    this._currentField.queries.forEach(query => {
      if (query.showDelete === true) {
        result = true;
        return;
      }
    });
    return result;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  encapsulation: ViewEncapsulation.None,
  selector: 'pfng-filter-fields',
  template: `<div class="filter-pf filter-fields"><div class="input-group form-group"><div class="input-group-btn" dropdown><button type="button" class="btn btn-default filter-fields dropdown-toggle" dropdownToggle tooltip="Filter by" placement="{{config?.tooltipPlacement}}" [disabled]="config.disabled === true">{{currentField?.title}} <span aria-hidden="true" class="caret"></span></button><ul class="dropdown-menu" role="menu" *dropdownMenu><li role="menuitem" *ngFor="let field of config?.fields" [ngClass]="{'disabled': isFieldDisabled(field), 'divider dropdown-divider': field.separator}"><a class="filter-field dropdown-item" href="javascript:void(0);" role="menuitem" tabindex="-1" (click)="selectField(field)" *ngIf="!field?.separator && !isFieldDisabled(field)">{{field?.title}}</a> <a class="filter-field dropdown-item" href="javascript:void(0);" role="menuitem" onclick="return false;" *ngIf="!field?.separator && isFieldDisabled(field)">{{field?.title}}</a></li></ul></div><div *ngIf="!currentField?.type || currentField?.type === 'text' || currentField.type === 'default'"><input class="form-control" type="{{currentField?.type}}" [(ngModel)]="currentValue" placeholder="{{currentField?.placeholder}}" [disabled]="config.disabled === true" (keypress)="fieldInputKeyPress($event)"></div><div *ngIf="currentField?.type === 'select'"><div class="btn-group bootstrap-select form-control filter-select" dropdown><button type="button" class="btn btn-default dropdown-toggle" dropdownToggle [disabled]="config.disabled === true"><span class="filter-option pull-left" [ngClass]="{'placeholder': !currentValue}">{{currentValue || currentField?.placeholder}} </span><span aria-hidden="true" class="caret"></span></button><ul class="dropdown-menu" role="menu" *dropdownMenu><li role="menuitem" *ngFor="let query of currentField?.queries" [ngClass]="{'selected': query?.value === currentValue, 'divider dropdown-divider': query?.separator}"><a class="dropdown-item" href="javascript:void(0);" tabindex="-1" (click)="selectQuery(query)" *ngIf="!query?.separator"><span class="{{query?.iconStyleClass}}" *ngIf="query?.iconStyleClass"></span> <img class="avatar" [attr.src]="query?.imageUrl" *ngIf="query?.imageUrl"> {{query.value}}</a></li></ul></div></div><div *ngIf="currentField?.type === 'typeahead'"><div class="btn-group bootstrap-select form-control filter-select" *ngIf="config.disabled === true"><div class="pull-left typeahead-input-container disabled"><input class="form-control" type="text" placeholder="{{currentField?.placeholder}}" [disabled]="config.disabled === true"> <span class="caret"></span></div></div><div class="btn-group bootstrap-select form-control filter-select" dropdown (isOpenChange)="hideDeleteConfirm($event)" *ngIf="config.disabled !== true"><div class="pull-left typeahead-input-container dropdown-toggle" dropdownToggle><input #queryInput class="form-control" type="text" placeholder="{{currentField?.placeholder}}" [(ngModel)]="currentValue" (ngModelChange)="queryInputChange($event)"> <span (click)="queryInput.focus()" class="caret"></span></div><ul class="dropdown-menu" role="menu" *dropdownMenu><li role="menuitem" *ngFor="let query of currentField?.queries" [ngClass]="{'selected': query.value === currentValue,
                          'divider dropdown-divider': query?.separator,
                          'pfng-filter-delete-wrapper': query?.showDelete}"><div class="pfng-filter-delete-slide" [ngClass]="{'slide-in': query?.showDeleteConfirm}" *ngIf="query?.showDelete"><span class="pfng-filter-delete-text">Delete filter?</span> <span class="pfng-filter-delete-confirm close"><a class="padding-right-5" href="javascript:void(0);" tabindex="-1" (click)="deleteQueryConfirm($event, query)"><span class="fa fa-check"></span> </a></span><span class="pfng-filter-delete-confirm close"><a class="padding-right-5" href="javascript:void(0);" tabindex="-1" (click)="deleteQueryCancel($event, query)"><span class="fa fa-remove"></span></a></span></div><a #blurable class="dropdown-item" href="javascript:void(0);" tabindex="-1" (click)="selectQuery(query)" *ngIf="!query?.separator"><span class="pfng-filter-delete close" *ngIf="query?.showDelete"><a href="javascript:void(0);" tabindex="-1" [ngClass]="{'hidden': query?.showDeleteConfirm}" (click)="deleteQuery($event, query, blurable)"><span class="pficon pficon-remove"></span> </a></span><span class="{{query?.iconStyleClass}}" *ngIf="query?.iconStyleClass"></span> <img class="avatar" [attr.src]="query?.imageUrl" *ngIf="query?.imageUrl"> <span [innerHTML]="query.value | truncate: 20 | searchHighlight: queryInput.value"></span></a></li></ul></div></div></div></div>`
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'config': [{ type: Input },],
'onAdd': [{ type: Output, args: ['onAdd', ] },],
'onDelete': [{ type: Output, args: ['onDelete', ] },],
'onFieldSelect': [{ type: Output, args: ['onFieldSelect', ] },],
'onTypeAhead': [{ type: Output, args: ['onTypeAhead', ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
